import {IconButton} from '#/components/buttons/icon-button';
import {ReactComponent as AppleLogo} from '#/components/icons/apple-logo.svg';
import {ReactComponent as FacebookLogo} from '#/components/icons/facebook-logo.svg';
import {ReactComponent as GoogleLogo} from '#/components/icons/google-logo.svg';
import {ReactComponent as MetamaskLogo} from '#/components/icons/logo-metamask.svg';
import {ReactComponent as RedditLogo} from '#/components/icons/reddit-logo.svg';
import {ReactComponent as XLogo} from '#/components/icons/x-logo.svg';
import {WalletContext} from '#/contexts/wallet-provider';
import environment from '#/environment/environment';
import {ClassName} from '#/utils/utils';
import {ArrowRightIcon} from '@heroicons/react/24/solid';
import {WALLET_ADAPTERS} from '@web3auth/base';
import {LOGIN_PROVIDER, LOGIN_PROVIDER_TYPE} from '@web3auth/openlogin-adapter';
import {ChangeEvent, ComponentProps, FunctionComponent, useContext, useState} from 'react';

export const LoginGroup: FunctionComponent<ClassName> = ({className}) => {
  return (
    <div className={`w-full space-y-3 ${className}`}>
      {environment.loginOpenLoginEnabled && (
        <>
          <EmailLoginGroup />
          <SocialLoginGroup />
        </>
      )}

      {environment.loginOpenLoginEnabled && environment.loginMetamaskEnabled && (
        <div className='h-fit'>
          <div className='w-full h-px bg-gray-300 my-2'></div>
        </div>
      )}
      {environment.loginMetamaskEnabled && <MetamaskLoginGroup />}
    </div>
  );
};

const EmailLoginGroup: FunctionComponent = () => {
  const {connectWithWeb3Auth} = useContext(WalletContext);

  const [email, setEmail] = useState('');
  const [validEmail, setValidEmail] = useState(false);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setValidEmail(!!event.target.value && event.target.validity.valid);
  };

  const onClick = () => {
    connectWithWeb3Auth({
      walletName: WALLET_ADAPTERS.OPENLOGIN,
      loginParams: {
        loginProvider: LOGIN_PROVIDER.EMAIL_PASSWORDLESS,
        extraLoginOptions: {
          login_hint: email,
        },
      },
    });
  };

  return (
    <div className='w-full flex gap-2'>
      <input
        className='min-w-0 grow outline-none bg-transparent px-5 py-1 rounded-full invalid:focus:ring-downred text-black text-base ring-1 ring-gray-400 focus:ring-deepblue'
        type='email'
        placeholder='Login with email'
        value={email}
        onChange={onChange}
      />
      <IconButton
        className='w-14 h-14 p-4 shrink-0 grow-0 flex justify-center items-center rounded-full bg-deepblue disabled:grayscale disabled:opacity-50 hover:opacity-70'
        IconComponent={ArrowRightIcon}
        iconClassName='w-full fill-white stroke-white'
        disabled={!validEmail}
        onClick={onClick}
      />
    </div>
  );
};

const SocialLoginGroup: FunctionComponent = () => {
  return (
    <div className='w-full flex flex-wrap justify-between gap-2'>
      <GoogleButton />
      <AppleButton />
      <FacebookButton />
      <RedditButton />
      <XButton />
    </div>
  );
};

const MetamaskLoginGroup: FunctionComponent = () => {
  const {connectWithWeb3Auth} = useContext(WalletContext);

  const onClick = () => {
    connectWithWeb3Auth({
      walletName: WALLET_ADAPTERS.METAMASK,
    });
  };

  return (
    <IconButton
      className='w-full h-14 p-4 flex justify-center items-center rounded-full bg-transparent border border-gray-400 disabled:grayscale aspect-square hover:opacity-70 row-start-4 row-span-1 col-start-1 col-span-5'
      IconComponent={MetamaskLogo}
      iconClassName='h-full fill-white stroke-white'
      onClick={onClick}
    />
  );
};

const LoginIconButton: FunctionComponent<
  {loginProvider: LOGIN_PROVIDER_TYPE} & Pick<ComponentProps<typeof IconButton>, 'IconComponent'> &
    Pick<Partial<ComponentProps<typeof IconButton>>, 'iconClassName'>
> = ({loginProvider, IconComponent, iconClassName}) => {
  const {connectWithWeb3Auth} = useContext(WalletContext);

  const onClick = () => {
    connectWithWeb3Auth({
      walletName: WALLET_ADAPTERS.OPENLOGIN,
      loginParams: {
        loginProvider,
      },
    });
  };

  return (
    <IconButton
      className='w-14 h-14 p-4 flex justify-center items-center rounded-full bg-transparent border border-gray-400 aspect-square hover:opacity-70'
      IconComponent={IconComponent}
      iconClassName={`w-full h-full overflow-hidden ${iconClassName}`}
      onClick={onClick}
    />
  );
};

const GoogleButton: FunctionComponent = () => (
  <LoginIconButton loginProvider={LOGIN_PROVIDER.GOOGLE} IconComponent={GoogleLogo} />
);

const AppleButton: FunctionComponent = () => (
  <LoginIconButton
    loginProvider={LOGIN_PROVIDER.APPLE}
    IconComponent={AppleLogo}
    iconClassName='w-full grayscale contrast-0'
  />
);

const FacebookButton: FunctionComponent = () => (
  <LoginIconButton loginProvider={LOGIN_PROVIDER.FACEBOOK} IconComponent={FacebookLogo} />
);

const RedditButton: FunctionComponent = () => (
  <LoginIconButton loginProvider={LOGIN_PROVIDER.REDDIT} IconComponent={RedditLogo} />
);

const XButton: FunctionComponent = () => (
  <LoginIconButton
    loginProvider={LOGIN_PROVIDER.TWITTER}
    IconComponent={XLogo}
    iconClassName='h-fit grayscale contrast-0'
  />
);
